/**
 * Shared types for Banker Reconciliation components
 */

import type { EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import type { ReconFeeSource } from '@/utils/banker-recon/feePaymentLedger';

// ============================================================================
// KEY DATES
// ============================================================================

export interface KeyDates {
  closingDate: string;
  fundsToEscrow: string;
  fundsReceived: string;
  warehouseDays: number;
  dailyInterestRate: number;
}

// ============================================================================
// COSTS
// ============================================================================

export interface WarehouseCost {
  id: string;
  description: string;
  amount: number;
  reimbursable?: boolean;
}

export interface InvestorCost {
  id: string;
  description: string;
  amount: number;
  reimbursable?: boolean;
}

export interface CostsState {
  warehouse: WarehouseCost[];
  investor: InvestorCost[];
  flexcloseCredit: number;
  warehouseHaircutPercent: number;
}

// ============================================================================
// REVENUE
// ============================================================================

export interface LoanRevenue {
  id: string;
  description: string;
  amount: number;
  reimbursable?: boolean;
  fee_source?: ReconFeeSource | null;
  /** Retained-at-warehouse amount excluded from P&L (warehouse basis = amount + this). */
  warehouse_retained_amount?: number;
  /** Whether this loan-revenue row nets to the warehouse advance (dropdown / auto-captured fees). */
  nets_to_warehouse_advance?: boolean | null;
}

export interface InvestorRevenue {
  id: string;
  description: string;
  srpPercent?: number;
  amount: number;
}

export interface RevenueState {
  loan: LoanRevenue[];
  investor: InvestorRevenue[];
  lenderCredit: number;
  cureAppliedLenderCredit: number;
  lenderCreditNetsToWarehouseAdvance: boolean | null;
  cureAppliedLenderCreditNetsToWarehouseAdvance: boolean | null;
}

// ============================================================================
// PRICING & COMPENSATION
// ============================================================================

export interface PricingState {
  rateLockPrice: number;       // Base rate lock price (e.g., 102.771)
  profitMargin: number;        // Built-in margin (e.g., 0.572)
  actualPricePercent: number;  // From purchase advice
}

/**
 * A purchase price adjustment. `basisPoints` is misnamed — it's price points (the UI computes dollars as
 * loanAmount * basisPoints / 100). `extractedTargetPrice` is present only on the synthetic
 * `auto_extracted_price` row created by document auto-fill; the InvestorAdviceTab derive keeps that row's
 * basisPoints pinned to (extractedTargetPrice - investorLockPrice) so the actual price tracks the document.
 */
export interface PurchaseAdjustment {
  id: string;
  description: string;
  basisPoints: number;
  extractedTargetPrice?: number;
}

/** Stable id of the synthetic adjustment that document auto-fill uses to pin the actual purchase price. */
export const AUTO_EXTRACTED_PRICE_ID = 'auto_extracted_price';

/**
 * Sum of ALL |ProfitMargin| buy-side adjustments on the loan. A loan can carry several ProfitMargin rows;
 * summing them (not taking the first) is the source of truth for both the investor lock price and the
 * company margin percent shown/saved by the recon. Shared so the pricing refresh and `computeInvestorLockPrice`
 * agree (a `.find()` on the first row understates multi-row margins).
 */
export function sumProfitMargin(loan: EncompassLoan | null): number {
  return (loan?.rate_lock_buy_side_adjustments || [])
    .filter((adj) => adj.priceAdjustmentType === 'ProfitMargin')
    .reduce((sum, adj) => sum + Math.abs(adj.rate), 0);
}

/**
 * The margin-adjusted investor lock buy price: Encompass total buy price + Σ|ProfitMargin adjustments|.
 * Shared so the parent coordinator and InvestorAdviceTab derive the identical value (the actual purchase
 * price = investorLockPrice + Σ purchaseAdjustments).
 */
export function computeInvestorLockPrice(loan: EncompassLoan | null): number {
  const encompassBuyPrice = loan?.rate_lock_total_buy_price || 0;
  return encompassBuyPrice + sumProfitMargin(loan);
}

/**
 * Investor secondary-market fee types — the canonical list used by the Investor Advice tab dropdown, the
 * document auto-fill mapper/modal (LLM maps each extracted deduction to one of these ids), and the
 * extraction-feedback table. Kept here (not component-local) so all three import the same source.
 */
export const INVESTOR_FEE_OPTIONS = [
  { id: 'escrow_deposit', description: 'Escrow Deposit' },
  { id: 'flood_cert_fee', description: 'Flood Cert Fee' },
  { id: 'govt_funding_fee', description: 'Govt Funding Fee' },
  { id: 'interest_cost', description: 'Interest Cost' },
  { id: 'purchase_fee', description: 'Purchase Fee' },
  { id: 'tax_service_fee', description: 'Tax Service Fee' },
  { id: 'interest_rate_buydown', description: 'Interest Rate Buydown' },
  { id: 'underwriting_fee', description: 'Underwriting Fee' }
] as const;

/** An investor fee option id (e.g. `interest_cost`). */
export type InvestorFeeOptionId = (typeof INVESTOR_FEE_OPTIONS)[number]['id'];

export type CompensationDeductionType = 'lender_credit' | 'cure_applied_lender_credit' | 'lo_comp_reduction';
export type ManualCompensationDeductionType = Exclude<CompensationDeductionType, 'lo_comp_reduction'>;

export interface CompensationDeduction {
  type: CompensationDeductionType;
  label: string;
  sourceAmount: number;   // auto-populated from file
  deductionAmount: number; // user-adjustable (≤ sourceAmount)
  readOnly?: boolean;
  source?: 'product_pricing' | 'manual' | 'saved';
}

export const DEDUCTION_OPTIONS: { type: CompensationDeductionType; label: string }[] = [
  { type: 'lender_credit', label: 'Lender Credit' },
  { type: 'cure_applied_lender_credit', label: 'Cure Applied to Lender Credit' },
  { type: 'lo_comp_reduction', label: 'LO Comp Reduction' }
];

export interface CompensationState {
  type: 'flat_fee' | 'percentage';
  value: number;
  notes: string;
  deductions: CompensationDeduction[];
}

// ============================================================================
// DOCUMENTS
// ============================================================================

export type BankerDocumentType = 'purchase_advice' | 'warehouse_reconciliation' | 'combined' | 'unknown';

/**
 * Fields returned by the `banker-recon-extract` edge function (per doc type). The purchase price is
 * normalized to `targetActualPurchasePrice` across all doc types. `srpPremium` is evidence-only on the
 * client (the premium flows through the purchase price), and `dailyRate` is intentionally NOT applied.
 */
export interface ExtractedFields {
  // Purchase advice fields
  targetActualPurchasePrice?: number;
  investorName?: string;
  purchaseDate?: string;
  firstPaymentToInvestor?: string;
  srpPremium?: number; // evidence-only — never written to the form
  // Warehouse reconciliation fields
  closingDate?: string;
  fundsToEscrowDate?: string;
  fundsReceivedDate?: string;
  warehouseDays?: number;
  warehouseInterest?: number;
  fundingFee?: number;
  reconciliationFee?: number;
  delegatedUWFee?: number;
  collateralDwellFee?: number;
  shortageFunded?: number;
  flexCloseCredit?: number;
  // Wires (v3). Extracted only when present; the warehouse/combined doc's "Gross Amount Received from
  // Investor" wins for wireFromInvestor (the PA's "Purchase Amount" is fallback-only). The PA has no
  // wireFromWarehouse ("Net Proceeds to Originator via ACH" is warehouse-recon-only).
  wireFromInvestor?: number;
  wireFromWarehouse?: number;
  // Investor secondary-market deductions (v3). The model maps each doc line to an INVESTOR_FEE_OPTIONS id
  // and emits a positive amount; `label` is the raw doc wording (provenance / feedback training).
  investorCosts?: ExtractedInvestorCost[];
  // Common fields
  loanNumber?: string;
  borrowerName?: string;
  loanAmount?: number;
}

/** One extracted investor secondary-market deduction (v3). `type` is an `INVESTOR_FEE_OPTIONS` id. */
export interface ExtractedInvestorCost {
  type: string;
  label: string;
  amount: number;
}

/** Per-field extraction confidence (0-1), keyed by `ExtractedFields` field name. */
export type FieldConfidence = Record<string, number>;

/**
 * Client-side extractor version. MUST match `EXTRACTOR_VERSION` in the banker-recon-extract edge
 * function. It is part of the review key, so bumping it (when the field set/prompts change) invalidates
 * cached extractions + review markers and re-triggers the modal.
 */
export const BANKER_EXTRACTOR_VERSION = 'v5';

/** Classification result echoed back by the edge function. */
export interface BankerDocumentClassification {
  documentType: BankerDocumentType;
  confidence: number;
  reasoning: string;
}

/**
 * Cached extraction result persisted at `loan_documents.metadata.autoFillExtraction`. Discriminated on
 * `status` so the coordinator can reuse a cached result (or know a doc was ignored) without re-invoking.
 */
export type AutoFillExtractionCache =
  | {
      status: 'extracted';
      reviewKey: string;
      extractorVersion: string;
      documentType: BankerDocumentType;
      classification: BankerDocumentClassification | null;
      fields: ExtractedFields;
      fieldConfidence: FieldConfidence;
    }
  | {
      status: 'ignored';
      reviewKey: string;
      extractorVersion: string;
      reason: string;
    };

/** Review marker persisted at `loan_documents.metadata.autoFillReview` (written only after Save / Skip). */
export interface AutoFillReviewMark {
  reviewKey: string;
  status: 'applied' | 'dismissed';
  reviewedAt: string;
  extractorVersion: string;
}

/** One banker-recon document with its extracted text + auto-fill state (from `getReconDocumentTexts`). */
export interface ReconDocumentAutoFill {
  documentId: string;
  documentType: BankerDocumentType;
  extractedText: string | null;
  storagePath: string | null;
  queueId: string | null;
  queueStatus: string | null;
  queueUpdatedAt: string | null;
  extractedCharCount: number | null;
  /** Stable, reprocess-sensitive key; null when there's no extracted text yet. */
  reviewKey: string | null;
  /** True when a persisted review marker matches the current reviewKey. */
  alreadyReviewed: boolean;
  /** Cached extraction for the current reviewKey, if present. */
  autoFillExtraction: AutoFillExtractionCache | null;
}

export interface UploadedDocument {
  id: string;
  file: File | null;
  fileName?: string;
  documentType: BankerDocumentType;
  confidence: number;
  storagePath: string;
  extractedFields?: ExtractedFields;
  uploadedAt: Date;
  queueId?: string;
  progress?: number;
  processingStatus?: 'queued' | 'extracting' | 'classifying' | 'complete' | 'failed';
}

// ============================================================================
// UI STATE
// ============================================================================

export interface ExpandedSections {
  loanInfo: boolean;
  keyDates: boolean;
  costs: boolean;
  revenue: boolean;
  compensation: boolean;
  documents: boolean;
}

export interface ValidationIssue {
  type: 'warning' | 'error' | 'info';
  message: string;
}

// ============================================================================
// SHARED PROPS FOR TAB COMPONENTS
// ============================================================================

export interface BankerReconTabProps {
  selectedLoan: EncompassLoan | null;
  reconId: string | null;

  // Key dates
  keyDates: KeyDates;
  setKeyDates: React.Dispatch<React.SetStateAction<KeyDates>>;

  // Costs
  costs: CostsState;
  setCosts: React.Dispatch<React.SetStateAction<CostsState>>;

  // Revenue
  revenue: RevenueState;
  setRevenue: React.Dispatch<React.SetStateAction<RevenueState>>;

  // Pricing
  pricing: PricingState;
  setPricing: React.Dispatch<React.SetStateAction<PricingState>>;

  // Compensation
  compensation: CompensationState;
  setCompensation: React.Dispatch<React.SetStateAction<CompensationState>>;

  // Documents
  uploadedDocuments: UploadedDocument[];
  setUploadedDocuments: React.Dispatch<React.SetStateAction<UploadedDocument[]>>;

  // Auto-fill: a tab calls this when one of its docs finishes Docling extraction
  // (realtime queue update → status 'extracted'/'completed' with text). The parent-owned
  // coordinator (BankerRecon.tsx) serializes extraction + the review modal from here.
  onDocumentReady?: (documentId: string) => void;

  // Calculated values (passed down for display)
  totalWarehouseCosts: number;
  totalInvestorCosts: number;
  totalLoanRevenue: number;
  totalInvestorRevenue: number;
  loPayout: number;
  companyRevenue: number;
}

// ============================================================================
// HELPER TYPE GUARDS
// ============================================================================

/**
 * Safe numeric coercion - handles strings from Supabase
 * Supabase returns numeric columns as strings, which can cause
 * string concatenation instead of numeric addition.
 * Also strips commas from formatted numbers like "4,500.00"
 */
export const toNumber = (v: unknown, fallback = 0): number => {
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : fallback;
  }
  // Strip commas from formatted numbers before parsing
  const cleaned = typeof v === 'string' ? v.replace(/,/g, '') : v;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
};

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

export const formatCurrency = (amount: number): string => {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absAmount);

  return amount < 0 ? `(${formatted})` : formatted;
};

export const formatPercent = (value: number, decimals: number = 2): string => {
  return `${value.toFixed(decimals)}%`;
};

export const formatNumberWithCommas = (value: number | string): string => {
  if (value === '' || value === null || value === undefined) {
return '';
}
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) {
return '';
}

  const parts = num.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

export const parseFormattedNumber = (value: string): number => {
  const cleaned = value.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// ============================================================================
// DEFAULT STATE VALUES
// ============================================================================

export const DEFAULT_KEY_DATES: KeyDates = {
  closingDate: '',
  fundsToEscrow: '',
  fundsReceived: '',
  warehouseDays: 0,
  dailyInterestRate: 0
};

export const DEFAULT_COSTS: CostsState = {
  warehouse: [
    { id: 'shortage_funded', description: 'Shortage Funded at Closing', amount: 0 },
    { id: 'warehouse_interest', description: 'Warehouse Interest', amount: 0 },
    { id: 'warehouse_funding_fee', description: 'Warehouse Funding Fee', amount: 0 },
    { id: 'warehouse_recon_fee', description: 'Reconciliation Report Fee', amount: 0 },
    { id: 'warehouse_du_fee', description: 'Delegated Underwriter Fee', amount: 0 },
    { id: 'collateral_dwell_fee', description: 'Collateral Dwell Fee', amount: 0 }
  ],
  investor: [],
  flexcloseCredit: 0,
  warehouseHaircutPercent: 0
};

export const DEFAULT_REVENUE: RevenueState = {
  loan: [
    { id: 'processing_fee', description: 'Processing Fee (801c)', amount: 0 },
    { id: 'underwriting_fee', description: 'Underwriting Fee (801d)', amount: 0 },
    { id: 'interest_charged', description: 'Interest Charged at Closing', amount: 0 }
  ],
  investor: [
    { id: 'srp_premium', description: 'SRP Premium', srpPercent: 0, amount: 0 },
    { id: 'interest_credit', description: 'Interest To/From Seller', amount: 0 }
  ],
  lenderCredit: 0,
  cureAppliedLenderCredit: 0,
  lenderCreditNetsToWarehouseAdvance: false,
  cureAppliedLenderCreditNetsToWarehouseAdvance: false
};

export const DEFAULT_COMPENSATION: CompensationState = {
  type: 'flat_fee',
  value: 0,
  notes: '',
  deductions: []
};

export const DEFAULT_PRICING: PricingState = {
  rateLockPrice: 0,
  profitMargin: 0,
  actualPricePercent: 0
};

export const DEFAULT_EXPANDED_SECTIONS: ExpandedSections = {
  loanInfo: true,
  keyDates: true,
  costs: true,
  revenue: true,
  compensation: true,
  documents: true
};
